/**
 * SegmentBuilder - Construtor visual de segmentos para campanhas
 * 
 * Permite criar audiências dinâmicas usando filtros combinados (AND/OR).
 * Filtros de CRM só aparecem se crmEnabled = true.
 * 
 * @author Sistema de Campanhas
 * @version 1.0.0
 */

import { useCallback } from 'react';
import { Plus, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SegmentFilterGroup } from './SegmentFilterGroup';
import { SegmentPreview } from './SegmentPreview';
import {
  CampaignSegment,
  SegmentFilterGroup as FilterGroupType,
  SegmentPreviewContact,
  createEmptyGroup,
  isSegmentValid,
} from '@/types/campaign-segments';
import { useCampaignSegmentOptions } from '@/hooks/useCampaignSegmentOptions';
import { useCampaignSegmentPreview } from '@/hooks/useCampaignSegmentPreview';

interface SegmentBuilderProps {
  segment: CampaignSegment;
  onSegmentChange: (segment: CampaignSegment) => void;
  onContactsChange: (contacts: SegmentPreviewContact[]) => void;
  crmEnabled: boolean;
}

/**
 * Componente principal do construtor de segmentos
 */
export function SegmentBuilder({
  segment,
  onSegmentChange,
  onContactsChange,
  crmEnabled,
}: SegmentBuilderProps) {
  // Buscar opções dos filtros
  const options = useCampaignSegmentOptions(crmEnabled);
  
  // Buscar preview dos contatos
  const preview = useCampaignSegmentPreview(segment, true);

  // Sincronizar contatos com o componente pai quando mudar
  const prevContactsRef = useCallback(() => {
    onContactsChange(preview.contacts);
  }, [preview.contacts, onContactsChange]);
  
  // Atualizar contatos quando preview mudar
  if (preview.contacts.length > 0 || preview.totalCount > 0) {
    prevContactsRef();
  }

  /**
   * Adiciona um novo grupo (OR)
   */
  const handleAddGroup = () => {
    onSegmentChange({
      ...segment,
      groups: [...segment.groups, createEmptyGroup()],
    });
  };

  /**
   * Atualiza um grupo específico
   */
  const handleGroupChange = (index: number, group: FilterGroupType) => {
    const newGroups = [...segment.groups];
    newGroups[index] = group;
    onSegmentChange({ ...segment, groups: newGroups });
  };

  /**
   * Remove um grupo específico
   */
  const handleGroupRemove = (index: number) => {
    if (segment.groups.length <= 1) return; // Manter pelo menos 1 grupo
    
    onSegmentChange({
      ...segment,
      groups: segment.groups.filter((_, i) => i !== index),
    });
  };

  const hasValidFilters = isSegmentValid(segment);

  return (
    <div className="space-y-4">
      {/* Header com descrição */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Filter className="h-4 w-4" />
        <span>
          Configure os filtros para selecionar contatos da sua base de dados.
          {crmEnabled && ' Filtros de CRM estão disponíveis.'}
        </span>
      </div>

      {/* Grupos de filtros */}
      <div className="space-y-4">
        {segment.groups.map((group, index) => (
          <div key={group.id}>
            {/* Indicador OR entre grupos */}
            {index > 0 && (
              <div className="flex items-center gap-3 py-2">
                <div className="flex-1 border-t border-dashed" />
                <span className="text-sm font-medium text-primary bg-primary/10 px-3 py-1 rounded-full">
                  OU
                </span>
                <div className="flex-1 border-t border-dashed" />
              </div>
            )}
            
            <SegmentFilterGroup
              group={group}
              groupIndex={index}
              onChange={(g) => handleGroupChange(index, g)}
              onRemove={() => handleGroupRemove(index)}
              options={options}
              crmEnabled={crmEnabled}
              isOnlyGroup={segment.groups.length === 1}
            />
          </div>
        ))}
      </div>

      {/* Botão adicionar grupo OR */}
      <Button
        type="button"
        variant="outline"
        onClick={handleAddGroup}
        className="w-full gap-2 border-dashed"
      >
        <Plus className="h-4 w-4" />
        Adicionar grupo (OU)
      </Button>

      {/* Preview dos contatos */}
      <SegmentPreview
        contacts={preview.contacts}
        totalCount={preview.totalCount}
        isTruncated={preview.isTruncated}
        isLoading={preview.isLoading || options.isLoading}
        isError={preview.isError}
        hasValidFilters={hasValidFilters}
        onRefresh={preview.refetch}
      />
    </div>
  );
}

// Export nomeado adicional
export { SegmentBuilder as default };
